import { useState, useCallback, useRef } from 'react';
import { notify } from '../utils/toast';

export const useVoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const audioBlob = useRef<Blob | null>(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1, // Mono audio for better speech recognition
          sampleRate: 48000, // Higher sample rate
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Check if the browser supports the Opus codec
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'  // Prefer Opus for better quality
        : 'audio/webm';  // Fallback to default webm

      mediaRecorder.current = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000 // 128kbps for good quality
      });

      audioChunks.current = [];

      // Collect data more frequently for better quality
      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      // Start recording with smaller timeslices for more frequent data collection
      mediaRecorder.current.start(100);
      setIsRecording(true);
    } catch (error) {
      console.error('Failed to start recording:', error);
      notify.error('Failed to access microphone');
    }
  }, []);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!mediaRecorder.current || mediaRecorder.current.state === 'inactive') {
        setIsRecording(false);
        resolve(null);
        return;
      }

      mediaRecorder.current.onstop = () => {
        // Create a blob with the collected audio chunks
        const blob = new Blob(audioChunks.current, { 
          type: mediaRecorder.current?.mimeType || 'audio/webm'
        });

        // Stop all audio tracks
        const tracks = mediaRecorder.current?.stream.getTracks();
        tracks?.forEach(track => track.stop());
        
        // Store the audio blob
        audioBlob.current = blob;
        
        setIsRecording(false);
        resolve(blob);
      };

      mediaRecorder.current.stop();
    });
  }, []);

  return {
    isRecording,
    startRecording,
    stopRecording,
    mediaRecorder: mediaRecorder.current,
    audioBlob: audioBlob.current
  };
};
